import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  ManagementService,
  OrganisationService,
  StateBasedNavigationService,
  userHasPermissions$,
} from '@principle-theorem/ng-principle-shared';
import { LoggerService, TrackByFunctions } from '@principle-theorem/ng-shared';
import { PracticeMigration } from '@principle-theorem/practice-migrations';
import { PracticeSetupWizard } from '@principle-theorem/principle-core';
import {
  FUNDING_REQUESTS_FEATURE,
  ReportingPermissions,
  SETUP_WIZARD_FEATURE,
} from '@principle-theorem/principle-core/features';
import {
  type IBrand,
  type IPractice,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  asBoolean,
  filterUndefined,
  firstResult,
  getEnumValues,
  isPathChanged$,
  isSameRef,
  where,
  type WithRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SidebarBadges } from './sidebar-badges';
import {
  BRAND_SLUG,
  PRACTICE_SLUG,
  type ISidebarItem,
  type SidebarItem,
} from './sidebar-item';
import { SidebarItems, type ISideBarItemScope } from './sidebar-items';
import {
  FUNDING_REQUESTS_SIDEBAR_CONFIG,
  MANAGEMENT_SIDEBAR_CONFIG,
  MIGRATION_SIDEBAR_CONFIG,
  OPERATIONS_SIDEBAR_CONFIG,
  REPORTING_SIDEBAR_CONFIG,
  SCHEDULING_SIDEBAR_CONFIG,
} from './sidebar-items-config';

interface ISideBarSection {
  label: string;
  routes: SidebarItem[];
}

@Component({
  selector: 'pr-sidebar-items',
  templateUrl: './sidebar-items.component.html',
  styleUrls: ['./sidebar-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidebarItemsComponent {
  trackByRoute = TrackByFunctions.label<ISidebarItem>();
  setupRoute$: Observable<ISideBarSection | undefined>;
  migrationRoutes$: Observable<ISideBarSection>;
  schedulingRoutes$: Observable<ISideBarSection>;
  operationRoutes$: Observable<ISideBarSection>;
  reportingRoutes$: Observable<ISideBarSection>;
  fundingRequestRoutes$: Observable<ISideBarSection>;
  inFundingRequestRegion$: Observable<boolean>;
  managementRoutes$: Observable<ISidebarItem[]>;
  hasReportingPermissions$: Observable<boolean>;
  hasMigrationPermissions$: Observable<boolean>;
  @Input() expanded = false;

  constructor(
    private _organisation: OrganisationService,
    private _management: ManagementService,
    private _stateNav: StateBasedNavigationService,
    private _logger: LoggerService
  ) {
    const staffer$ = this._organisation.staffer$;

    const hasAccessToSetupWizard$ = combineLatest([
      staffer$,
      this._organisation.practice$.pipe(
        filterUndefined(),
        switchMap((practice) => PracticeSetupWizard.getByPractice(practice))
      ),
      this._management.user$,
    ]).pipe(
      switchMap(([staffer, wizard, managementUser]) => {
        if (!wizard || wizard.dismissedAt || !staffer) {
          return of(false);
        }

        return PracticeSetupWizard.hasAccess(wizard, staffer, !!managementUser);
      })
    );

    this.setupRoute$ = combineLatest([
      this._organisation.brand$.pipe(filterUndefined()),
      this._organisation.practice$.pipe(filterUndefined()),
      staffer$,
      hasAccessToSetupWizard$,
    ]).pipe(
      switchMap(([brand, practice, staffer, hasAccessToSetupWizard]) => {
        if (!hasAccessToSetupWizard) {
          return of(undefined);
        }

        const section = this._rebuildSetupRoute(brand, practice, staffer);
        const firstRoute = first(section.routes);
        if (!firstRoute?.badge$) {
          return of(section);
        }

        return firstRoute.badge$.pipe(
          map((badge) =>
            badge === undefined ? { ...section, routes: [] } : section
          )
        );
      })
    );

    this.migrationRoutes$ = combineLatest([
      this._organisation.brand$.pipe(filterUndefined()),
      this._organisation.practice$.pipe(filterUndefined()),
      staffer$,
    ]).pipe(
      map(([brand, practice, staffer]) => ({
        label: `Migration`,
        routes: this._rebuildMigrationRoutes(brand, practice, staffer),
      }))
    );

    this.schedulingRoutes$ = combineLatest([
      this._organisation.brand$.pipe(filterUndefined()),
      this._organisation.practice$.pipe(filterUndefined()),
      staffer$,
    ]).pipe(
      map(([brand, practice, staffer]) => ({
        label: `Scheduling`,
        routes: this._rebuildSchedulingRoutes(brand, practice, staffer),
      }))
    );

    this.operationRoutes$ = combineLatest([
      this._organisation.brand$.pipe(filterUndefined()),
      this._organisation.practice$.pipe(filterUndefined()),
      staffer$,
    ]).pipe(
      map(([brand, practice, staffer]) => ({
        label: `Operations`,
        routes: this._rebuildOperationalRoutes(brand, practice, staffer),
      }))
    );

    this.reportingRoutes$ = of({
      label: 'Reporting',
      routes: this._rebuildReportingRoutes(),
    });

    this.hasReportingPermissions$ = combineLatest([
      of(getEnumValues(ReportingPermissions)),
      this._organisation.userPermissions$,
      this._management.user$.pipe(isPathChanged$('ref.path')),
    ]).pipe(userHasPermissions$());

    this.hasMigrationPermissions$ = this._determineMigrationAccess();

    this.managementRoutes$ = this._management.user$.pipe(
      asBoolean(),
      map((isManager) => (isManager ? MANAGEMENT_SIDEBAR_CONFIG : []))
    );

    this.fundingRequestRoutes$ = combineLatest([
      this._organisation.brand$.pipe(filterUndefined()),
      this._organisation.practice$.pipe(filterUndefined()),
    ]).pipe(
      map(([brand, practice]) => ({
        label: 'Funding Requests',
        routes: this._rebuildFundingRequestRoutes(brand, practice),
      }))
    );

    this.inFundingRequestRegion$ = this._organisation.region$.pipe(
      filterUndefined(),
      map((region) => !!FUNDING_REQUESTS_FEATURE.regions?.includes(region))
    );
  }

  async loadPractice(practice: WithRef<IPractice>): Promise<void> {
    await this._stateNav.practice([], {}, practice);
    window.location.reload();
  }

  private _determineMigrationAccess(): Observable<boolean> {
    return combineLatest([
      this._management.user$.pipe(isPathChanged$('ref.path'), asBoolean()),
      this._organisation.staffer$.pipe(isPathChanged$('ref.path')),
      this._organisation.organisation$.pipe(
        filterUndefined(),
        isPathChanged$('ref.path'),
        switchMap((organisation) =>
          firstResult(
            PracticeMigration.col(),
            where('configuration.organisation.ref', '==', organisation.ref)
          )
        )
      ),
    ]).pipe(
      map(([isManager, staffer, migration]) => {
        if (!migration) {
          return false;
        }

        if (isManager) {
          return true;
        }

        if (!migration?.configuration.restrictAccessTo) {
          return false;
        }

        return migration.configuration.restrictAccessTo.some((restrictedRef) =>
          isSameRef(restrictedRef, staffer)
        );
      })
    );
  }

  private _rebuildMigrationRoutes(
    brand: WithRef<IBrand>,
    practice: WithRef<IPractice>,
    staffer?: WithRef<IStaffer>
  ): SidebarItem[] {
    const scope: ISideBarItemScope = {
      brand,
      practice,
      staffer,
      userPractices$: this._organisation.userPractices$,
      logger: this._logger,
    };
    const schedulingItems = new SidebarItems(MIGRATION_SIDEBAR_CONFIG);
    return schedulingItems.get(scope);
  }

  private _rebuildSchedulingRoutes(
    brand: WithRef<IBrand>,
    practice: WithRef<IPractice>,
    staffer?: WithRef<IStaffer>
  ): SidebarItem[] {
    const scope: ISideBarItemScope = {
      brand,
      practice,
      staffer,
      userPractices$: this._organisation.userPractices$,
      logger: this._logger,
    };
    const schedulingItems = new SidebarItems(SCHEDULING_SIDEBAR_CONFIG);
    return schedulingItems.get(scope);
  }

  private _rebuildOperationalRoutes(
    brand: WithRef<IBrand>,
    practice: WithRef<IPractice>,
    staffer?: WithRef<IStaffer>
  ): SidebarItem[] {
    const scope: ISideBarItemScope = {
      brand,
      practice,
      staffer,
      userPractices$: this._organisation.userPractices$,
      stafferSettings$: this._organisation.stafferSettings$,
      logger: this._logger,
    };
    const operationsItems = new SidebarItems(OPERATIONS_SIDEBAR_CONFIG);
    return operationsItems.get(scope);
  }

  private _rebuildReportingRoutes(): SidebarItem[] {
    const items = new SidebarItems(REPORTING_SIDEBAR_CONFIG);
    return items.get({ logger: this._logger });
  }

  private _rebuildFundingRequestRoutes(
    brand: WithRef<IBrand>,
    practice: WithRef<IPractice>
  ): SidebarItem[] {
    const scope: ISideBarItemScope = {
      brand,
      practice,
      userPractices$: this._organisation.userPractices$,
      logger: this._logger,
    };
    const items = new SidebarItems(FUNDING_REQUESTS_SIDEBAR_CONFIG);
    return items.get(scope);
  }

  private _rebuildSetupRoute(
    brand: WithRef<IBrand>,
    practice: WithRef<IPractice>,
    staffer?: WithRef<IStaffer>
  ): ISideBarSection {
    const schedulingItems = new SidebarItems([
      {
        label: 'Setup Wizard',
        path: `/${BRAND_SLUG}/${PRACTICE_SLUG}/setup`,
        display: { icon: 'rocket_launch' },
        feature: SETUP_WIZARD_FEATURE,
        resolveBadge: (badges: SidebarBadges) => badges.getSetupWizardBadge(),
      },
    ]);

    const scope: ISideBarItemScope = {
      brand,
      practice,
      staffer,
      userPractices$: this._organisation.userPractices$,
      logger: this._logger,
    };

    return {
      label: 'Setup',
      routes: schedulingItems.get(scope),
    };
  }
}
